import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, cld } from "@/lib/api";
import { FeaturedCarousel } from "@/components/site/FeaturedCarousel";
import { Newsletter } from "@/components/site/Newsletter";

const Hero = ({ hero }) => (
  <section className="container-x grid gap-12 pb-24 pt-12 sm:pt-20 md:grid-cols-12 md:gap-16" data-testid="home-hero">
    <div className="flex flex-col justify-end md:col-span-5 md:pb-10">
      <p className="eyebrow fade-up">Laszlo Abonyi — Paintings</p>
      <h1 className="fade-up mt-8 font-display text-5xl font-light leading-[1.02] sm:text-6xl lg:text-7xl" style={{ animationDelay: "120ms" }}>
        Light, architecture <em className="italic text-[#8C7A5B]">&amp;</em> stillness.
      </h1>
      <p className="fade-up mt-8 max-w-sm text-[15px] leading-relaxed text-[#5A626A]" style={{ animationDelay: "240ms" }}>
        Mid-century rooms, sun-soaked poolsides and quiet Australian bush — painted in oil, held in a moment between motion and rest.
      </p>
      <div className="fade-up mt-12 flex flex-wrap items-center gap-8" style={{ animationDelay: "360ms" }}>
        <Link to="/gallery" className="btn-primary" data-testid="hero-explore-button">View the collection</Link>
        <Link to="/about" className="link-u" data-testid="hero-about-link">About the artist</Link>
      </div>
    </div>
    <div className="md:col-span-7">
      {hero ? (
        <Link to={`/artwork/${hero.slug}`} className="group block" data-testid="hero-artwork-link">
          <div className="aspect-[4/5] overflow-hidden bg-[#F2EEE6] md:max-h-[82vh] md:w-auto">
            <img src={cld(hero.images[0], "w_1600,q_auto,f_auto")} alt={hero.title} className="slow-fade h-full w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.02]" />
          </div>
          <p className="mt-4 text-right text-xs tracking-[0.14em] text-[#5A626A]">
            <span className="font-display text-base italic tracking-normal text-[#1A1A1A]">{hero.title}</span> — original, oil
          </p>
        </Link>
      ) : (
        <div className="aspect-[4/5] animate-pulse bg-[#F2EEE6]" />
      )}
    </div>
  </section>
);

const Story = ({ image }) => (
  <section className="bg-[#F7F4EE]" data-testid="home-story">
    <div className="container-x grid items-center gap-14 py-24 sm:py-32 md:grid-cols-12">
      <div className="md:col-span-5">
        {image && <img src={cld(image, "w_1000,q_auto,f_auto")} alt="Surface detail of a painting by Laszlo Abonyi" className="aspect-[4/5] w-full object-cover" />}
      </div>
      <div className="md:col-span-6 md:col-start-7">
        <p className="eyebrow">The artist</p>
        <p className="mt-8 font-display text-3xl font-light leading-snug sm:text-4xl">
          “Each piece invites the viewer into a space of quiet contemplation.”
        </p>
        <p className="mt-8 max-w-lg text-[15px] leading-[1.8] text-[#5A626A]">
          Laszlo Abonyi is a contemporary painter whose work examines the intersection of architecture, memory, and atmosphere — mid-century spaces, twilight interiors and expansive landscapes, rendered with a rich, deliberate use of colour and shadow.
        </p>
        <Link to="/about" className="link-u mt-10" data-testid="story-read-more">Read the full story</Link>
      </div>
    </div>
  </section>
);

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  const hero = featured.find((p) => p.slug === "mulholland-after-five") || featured[0];
  const storyImg = featured.find((p) => p.slug === "forest-views")?.images?.[1] || featured[0]?.images?.[1];

  return (
    <div data-testid="home-page">
      <Hero hero={hero} />
      <section className="container-x py-24 sm:py-32" data-testid="home-featured">
        <div className="mb-14 flex items-end justify-between gap-6">
          <div>
            <p className="eyebrow">Selected works</p>
            <h2 className="mt-5 font-display text-4xl font-light sm:text-5xl">Original paintings</h2>
          </div>
          <Link to="/gallery?filter=originals" className="link-u hidden sm:inline-block" data-testid="featured-view-all">View all</Link>
        </div>
        {featured.length > 0 && <FeaturedCarousel items={featured} />}
      </section>
      <Story image={storyImg} />
      <Newsletter />
    </div>
  );
}
